"use client";

import React, { useRef, useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Quote, ChevronLeft, ChevronRight } from "@/lib/icons";
import { Button } from "@/components/ui/button";

// Custom hook for touch gestures
function useSwipe(onSwipeLeft?: () => void, onSwipeRight?: () => void) {
  const touchStart = useRef<number | null>(null);
  const touchEnd = useRef<number | null>(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    touchEnd.current = null;
    touchStart.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEnd.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStart.current || !touchEnd.current) return;
    
    const distance = touchStart.current - touchEnd.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && onSwipeLeft) onSwipeLeft();
    if (isRightSwipe && onSwipeRight) onSwipeRight();
  };

  return { onTouchStart, onTouchMove, onTouchEnd };
}

const testimonials = [
  {
    id: 1,
    content: "We have worked with DPCLR for over 30 years and we have no hesitation in offering both a glowing personal and professional reference.",
    author: "General Manager",
    position: "General Manager",
    company: "Care Home, Kent",
  },
  {
    id: 2,
    content: "For over 10 years, DPCLR has serviced the laundry equipment in our chain of hairdressing salons. We have always found Mr Perkins to be professional and reliable… He has consistently provided us with a prompt and efficient service.",
    author: "Business Owner",
    position: "Owner",
    company: "Large Hairdressing Chain",
  },
  {
    id: 3,
    content: "We are a soft fruit farm and employ over 200 casual employees during our harvest period who live on the farm. We provide laundry facilities to the staff and hire the washing machines and driers from Dave Perkins for this purpose. The hire includes an excellent repair and maintenance service…. We would always be very happy to recommend his service.",
    author: "Farm Owner",
    position: "Owner",
    company: "Fruit Farm, UK",
  },
  {
    id: 4,
    content: "Mr Perkins has, for the past 15 years, supplied and maintained our laundry machines. We have found him to always be highly professional and reliable.",
    author: "Farmer",
    position: "Farmer",
    company: "Kent",
  },
];

export function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const { onTouchStart, onTouchMove, onTouchEnd } = useSwipe(nextTestimonial, prevTestimonial);

  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      nextTestimonial();
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused]);

  return (
    <section 
      id="testimonials"
      aria-labelledby="testimonials-heading"
      className="py-20 px-4 bg-gradient-to-b from-white via-blue-50/15 to-blue-900/[0.04] relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,_rgba(59,130,246,0.08),_transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_30%,_rgba(59,130,246,0.06),_transparent_35%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_60%,_rgba(59,130,246,0.05),_transparent_45%)]" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-50/25 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-1/4 left-1/3 w-[400px] h-[400px] bg-sky-100/20 rounded-full blur-2xl" />
        <div className="absolute top-1/3 left-1/4 w-[300px] h-[300px] bg-blue-50/20 rounded-full blur-2xl" />
        <div className="absolute bottom-1/3 right-1/4 w-[250px] h-[250px] bg-blue-100/15 rounded-full blur-xl" />
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 sm:mb-12">
          <span className="text-blue-600 font-semibold text-sm sm:text-base uppercase tracking-wider mb-4 sm:mb-4 block" aria-hidden="true">
            Client Testimonials
          </span>
          <h2 id="testimonials-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-4">
            What Our Clients Say
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto" role="doc-subtitle">
            Don&apos;t just take our word for it - hear from the businesses who trust 
            DPCLR for their commercial laundry needs.
          </p>
        </div>

        {/* Mobile-friendly testimonials carousel */}
        <div 
          className="relative overflow-hidden py-8"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          role="region"
          aria-roledescription="carousel"
          aria-label="Client testimonials carousel"
        >
          <div 
            ref={containerRef}
            className="flex transition-transform duration-500 ease-out touch-pan-x"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id} 
                className="flex-shrink-0 w-full px-4"
                style={{ width: '100%' }}
                role="group"
                aria-roledescription="slide"
                aria-label={`${testimonials.findIndex(t => t.id === testimonial.id) + 1} of ${testimonials.length}`}
              >
                <Card className="border-0 shadow-lg h-full mx-auto max-w-2xl">
                  <CardContent className="p-8 sm:p-10">
                    <div className="relative">
                      <Quote className="absolute -top-2 -left-2 h-10 w-10 sm:h-12 sm:w-12 text-blue-100" aria-hidden="true" />
                      <div className="relative z-10">
                        <blockquote>
                          <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed mb-6 sm:mb-8 italic">
                            &quot;{testimonial.content}&quot;
                          </p>
                          <footer className="flex items-center">
                            <div>
                              <cite className="text-base sm:text-lg font-semibold text-gray-900 not-italic">
                                {testimonial.author}
                              </cite>
                              <p className="text-sm sm:text-base text-gray-600">
                                {testimonial.position}, {testimonial.company}
                              </p>
                            </div>
                          </footer>
                        </blockquote>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {/* Navigation controls */}
          <div 
            className="flex justify-center items-center mt-8 gap-6"
            role="group"
            aria-label="Carousel navigation"
          >
            <Button
              variant="outline"
              size="icon"
              className="rounded-full hover:bg-blue-50 border-blue-200 hover:border-blue-300 transition-all duration-200"
              onClick={prevTestimonial}
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <div 
              className="flex gap-4 px-2"
              role="tablist"
              aria-label="Select testimonial"
            >
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  role="tab"
                  aria-selected={index === currentIndex}
                  aria-label={`Go to testimonial ${index + 1}`}
                  className={`min-w-12 min-h-12 p-3 rounded-full transition-all duration-300 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    index === currentIndex 
                      ? 'bg-blue-50 ring-1 ring-blue-200' 
                      : 'hover:bg-blue-50/50'
                  }`}
                  onClick={() => setCurrentIndex(index)}
                >
                  <div
                    className={`rounded-full transition-all duration-300 ${
                      index === currentIndex 
                        ? 'bg-blue-600 w-5 h-2.5' 
                        : 'bg-blue-200 w-2.5 h-2.5 hover:bg-blue-300'
                    }`}
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full hover:bg-blue-50 border-blue-200 hover:border-blue-300 transition-all duration-200"
              onClick={nextTestimonial}
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>

          {/* Swipe indicator for mobile */}
          <div 
            className="mt-4 text-center text-sm text-gray-500 md:hidden"
            role="alert"
            aria-live="polite"
          >
            Swipe to see more testimonials
          </div>
        </div>
      </div>
    </section>
  );
}
